import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Eye, EyeOff, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import GoogleAuthButton from "@/components/vangard/GoogleAuthButton";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import LangSwitcher from "@/components/LangSwitcher";
import VMark from "@/components/vangard/VMark";
import Seo from "@/components/Seo";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, loginWithGoogleCredential } = useAuth();
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/account");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("vc.auth.login-fail"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white grid lg:grid-cols-2" data-testid="login-page">
      <Seo title={t("vc.seo.login.title")} description={t("vc.seo.login.desc")} noindex />
      {/* Left editorial column */}
      <aside className="relative hidden lg:flex flex-col justify-between p-12 border-r border-white/[0.06] overflow-hidden">
        <VMark position="bottom-left" size={720} opacity={0.06} />
        <Link to="/" className="font-serif text-[22px] relative z-10" data-testid="brand-link">
          Vangard<span className="text-neutral-400">Club</span>
        </Link>
        <div className="relative z-10 max-w-md">
          <div className="text-[11px] tracking-[0.35em] uppercase text-neutral-400">{t("vc.hero.eyebrow")}</div>
          <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] mt-6">
            <span className="italic text-neutral-300">{t("vc.auth.quote")}</span>
          </h1>
        </div>
        <div className="relative z-10 text-[10px] tracking-[0.35em] uppercase text-neutral-600">
          {t("vc.auth.quote-portal")}
        </div>
      </aside>

      {/* Right form column */}
      <main className="relative flex flex-col">
        <div className="flex items-center justify-between px-6 lg:px-12 py-6 border-b border-white/[0.06]">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-neutral-400 hover:text-white transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft size={16} />
            {t("common.back")}
          </button>
          <LangSwitcher />
        </div>

        <div className="flex-1 flex items-center px-6 lg:px-12 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-md mx-auto"
          >
            <div className="text-[11px] tracking-[0.35em] uppercase text-neutral-500">{t("vc.auth.member-portal")}</div>
            <h2 className="font-serif text-3xl lg:text-4xl mt-3 leading-[1.05]">
              {t("vc.auth.welcome-back")}
            </h2>
            <p className="mt-2 text-sm text-neutral-400 font-light leading-relaxed">
              {t("vc.auth.welcome-line")}
            </p>

            <form onSubmit={onSubmit} className="mt-7 space-y-4">
              <div>
                <label className="text-[11px] tracking-[0.3em] uppercase text-neutral-500">{t("vc.auth.email")}</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full mt-2 bg-transparent border-0 border-b border-white/15 text-white text-base font-light py-2.5 focus:border-white/60 focus:outline-none transition-colors placeholder:text-neutral-600"
                  placeholder="you@example.com"
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <label className="text-[11px] tracking-[0.3em] uppercase text-neutral-500">{t("vc.auth.password")}</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full mt-2 bg-transparent border-0 border-b border-white/15 text-white text-base font-light py-2.5 pr-8 focus:border-white/60 focus:outline-none transition-colors placeholder:text-neutral-600"
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-0 top-1/2 translate-y-1 text-neutral-500 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 inline-flex items-center justify-center gap-3 bg-white text-black px-8 py-3.5 rounded-full text-[12px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors disabled:opacity-50"
                data-testid="login-submit-btn"
              >
                {loading ? t("vc.auth.signing-in") : t("vc.auth.sign-in")}
                <ArrowRight size={14} />
              </button>
            </form>

            <div className="relative my-6 flex items-center gap-4">
              <span className="h-px flex-1 bg-white/10" />
              <span className="text-[11px] tracking-[0.35em] uppercase text-neutral-500">{t("vc.auth.or")}</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>

            <GoogleAuthButton
              label={t("vc.auth.continue-google")}
              onCredential={(cred) => loginWithGoogleCredential(cred).then(() => navigate("/account")).catch(() => toast.error(t("vc.auth.google-fail")))}
              onError={() => toast.error(t("vc.auth.google-fail"))}
              testid="login-google-btn"
            />

            <p className="text-center mt-6 text-sm text-neutral-400">
              {t("vc.auth.no-account")}{" "}
              <Link
                to="/register"
                className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-white transition-colors"
                data-testid="signup-link"
              >
                {t("vc.auth.join-vangard")}
              </Link>
            </p>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
